import { message } from "antd";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { ThemeProvider as EmotionThemeProvider } from "emotion-theming";
import { useHistory } from "react-router-dom";

import { postLogin, postVerify } from "apis/authentication";
import { updateApiHeaders } from "apis/apiHelper";
import { useGetUserById } from "apis/user";
import {
  getIsUserLoggedIn,
  getUser,
  setUserAuthObj,
  deleteUserAuthObj,
} from "utils/auth";
import { buildLoginUri } from "utils/routes";
import { isElement } from "react-dom/test-utils";
import { getAllProducts } from "apis/product";
import { getUserSubscription } from "apis/billing";

const AppContext = React.createContext();

const useTheme = () => {
  const originalTheme = useMemo(
    () => ({
      color: {
        primary: "#000000",
        complementary: "#D7A301",
        secondary: "#F2F2F2",
        red: "#EC373A",
        green: "#2AC940",
        grey: "#797979",
        text: "#4F4F4F",
        white: "#FFFFFF",
      },
      fontWeight: {
        light: 200,
        regular: 400,
        bold: 800,
      },
      spacing: {
        xxs: "4px",
        xs: "8px",
        regular: "16px",
        md: "32px",
        lg: "40px",
        xl: "48px",
        xxl: "60px",
        xxxl: "104px",
      },
    }),
    []
  );
  const [theme] = useState(originalTheme);

  return theme;
};

export const AppContextProvider = ({ children }) => {
  const history = useHistory();
  const [user, setUser] = useState({});
  const [localization, setLocalization] = useState("en-US");
  const [isLoadingUser, setIsLoading] = useState(true);
  const {
    isLoading: isLoggedInUserProfileLoading,
    data: loggedInUserProfile,
    refetch: refetchLoggedInUserProfile,
  } = useGetUserById(user._id, !!user._id);

  useEffect(() => {
    if (getIsUserLoggedIn()) {
      const storedUser = getUser();
      setUser(storedUser);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    refetchLoggedInUserProfile();
  }, [user]);

  const theme = useTheme();

  const onLogin = ({ username, password }) => {
    setIsLoading(true);
    return postLogin(username, password)
      .then((userData) => {
        setUserAuthObj(userData);
        setUser(userData.user);

        updateApiHeaders();

        return userData;
      })
      .finally(() => setIsLoading(false));
  };

  const onLogout = useCallback(() => {
    deleteUserAuthObj();
    setUser({});
    updateApiHeaders();

    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  const mountUserData = useCallback(async () => {
    if (getIsUserLoggedIn()) {
      await postVerify()
        .then(() => {
          const storedUser = getUser();
          setUser(storedUser);
        })
        .catch(() => {
          onLogout();
          message.error(
            "You've been inactive for awhile. We automatically sign you out for your security. Please log in again."
          );
          history.push(buildLoginUri());
        });
    }
    setIsLoading(false);
  }, [history, onLogout]);

  useEffect(() => {
    mountUserData();
  }, [mountUserData]);

  const getLoggedInUser = () => {
    return {
      _id: user._id,
      username: user.username,
      ...loggedInUserProfile,
      isLoggedInUserProfileLoading,
    };
  };

  const getLoggedInUserSubscription = async () => {
    const res = await getUserSubscription();

    console.log("res getLoggedInUserSubscription", res);

    if (res.status === 200) {
      if (res.subscription.length > 0) {
        return res.subscription;
      } else {
        return [];
      }
    } else {
      return [];
    }
  };

  const isUserLoading = isLoadingUser || isLoggedInUserProfileLoading;
  const isLoggedIn = !!user && !!user.username;

  const isAdmin = user.role === "admin";
  const isFree = user.role === "free";
  const isAgentEntry = user.role === "agent_entry";
  const isAgentFull = user.role === "agent_full";
  const isLeader = user.role === "leader";
  const isContributor = user.role === "contributor";
  const isAgentFullAndContributor = user.role === "agent_full_and_contributor";

  return (
    <AppContext.Provider
      value={{
        getLoggedInUser,
        getLoggedInUserSubscription,
        onLogin,
        onLogout,
        isUserLoading,
        isLoggedIn,
        isAdmin,
        isFree,
        isAgentEntry,
        isAgentFull,
        isLeader,
        isContributor,
        isAgentFullAndContributor,
        refetchLoggedInUserProfile,
        changeLocalization: (e) => setLocalization(e),
      }}
    >
      <EmotionThemeProvider theme={theme}>{children}</EmotionThemeProvider>
    </AppContext.Provider>
  );
};

export const AppContextConsumer = AppContext.Consumer;

export const withAppContext = (Component) => {
  const AppContextComponent = (props) => (
    <AppContextConsumer>
      {(appContextProps) => <Component {...appContextProps} {...props} />}
    </AppContextConsumer>
  );
  return AppContextComponent;
};
